import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import LocalStorageService from '../../../utility/LocalStorageService';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';
import { CrudType } from '../../../utility/types/CrudType';
import { CrudStatus } from '../../../utility/types/CrudStatus';

const selectIsAlertListLocalStorageLoaded = createSelector(
  (state) => state.alertList,
  (alertList) => alertList.isLocalStorageLoaded,
);

const selectAlertList = createSelector([
  (state) => state.alertList.alertList], (alertList) => alertList);

const selectAlertFromListById = createSelector([
  selectAlertList, (_, alertId) => alertId],
(alerts, alertId) => alerts.find((alert) => alert.id === alertId));

const selectAlertListPageNo = createSelector([
  (state) => state.alertList.currPage], (currPage) => currPage);

const selectAlertListTotalResults = createSelector([
  (state) => state.alertList.totalNumRecords], (totalNumRecords) => totalNumRecords);

const selectAllIds = createSelector([
  (state) => state.alertList.totalRecordsIdList], (totalRecordsIdList) => totalRecordsIdList);

const selectAlertListRowsPerPage = createSelector([
  (state) => state.alertList,
], (alertList) => alertList.rowsPerPage);

const selectAlertListColumnVisibility = createSelector([
  (state) => state.alertList,
], (alertList) => alertList.columnVisibility);

const selectAlertListCrudStatus = (state) => state.alertList.crudStatus;

const selectActiveSearchParams = createSelector([
  (state) => state.alertList,
], (alertList) => alertList.activeSearchParams);

const selectHasNextPage = createSelector([
  (state) => state.alertList,
], (alertList) => alertList.hasNextPage);

const fetchFilteredAlertList = createAsyncThunk(
  'securedApi/alertList/fetchAlertList',
  async (searchRequest, { rejectWithValue, getState }) => {
    try {
      const { rowsPerPage, currPage } = getState().alertList;
      const response = await RaaAuthenticatedRemoteService.globalInstance()
        .post(`${envConfig.apiUrl}/api/v1/search`,
          { data: { ...searchRequest, pageNo: currPage, pageSize: rowsPerPage } });
      return {
        ...response,
        searchRequest,
      };
    } catch (error) {
      return rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

const refreshFilteredAlertList = createAsyncThunk(
  'securedApi/alertList/refreshAlertList',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { rowsPerPage, currPage, activeSearchParams } = getState().alertList;
      return await RaaAuthenticatedRemoteService.globalInstance()
        .post(`${envConfig.apiUrl}/api/v1/search`,
          { data: { ...activeSearchParams, pageNo: currPage, pageSize: rowsPerPage } });
    } catch (error) {
      return rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

const initialState = {
  alertList: [],
  currPage: 0,
  hasNextPage: false,
  totalNumRecords: 0,
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  error: {},
  activeSearchParams: {
    id: null,
    name: null,
    filters: [],
    sort: null,
    sortType: null,
  },
  columnVisibility: {
    Name: true,
    Source: true,
    Tags: true,
    Trigger: true,
    Clear: true,
    Current: true,
    // Actions: true,
  },
  rowsPerPage: 50,
  isLocalStorageLoaded: false,
  totalRecordsIdList: [],
};

const alertListSlice = createSlice({
  name: 'alertList',
  initialState,
  reducers: {
    setRowsPerPage: (state, action) => {
      let rowsPerPage = action.payload;
      if (rowsPerPage > 250) {
        rowsPerPage = 250;
      } else if (rowsPerPage < 1) {
        rowsPerPage = 1;
      }
      state.rowsPerPage = rowsPerPage;
      state.currPage = 0;
      LocalStorageService.saveStateToLocalStorage(LocalStorageService.ALERT_LIST_KEY, {
        activeSearchParams: state.activeSearchParams,
        rowsPerPage: state.rowsPerPage,
        columnVisibility: state.columnVisibility,
        version: LocalStorageService.VERSION,
      });
    },
    setColumnVisibility: (state, action) => {
      const { columnName, visible } = action.payload;
      state.columnVisibility[columnName] = visible;
      LocalStorageService.saveStateToLocalStorage(LocalStorageService.ALERT_LIST_KEY, {
        activeSearchParams: state.activeSearchParams,
        rowsPerPage: state.rowsPerPage,
        columnVisibility: state.columnVisibility,
        version: LocalStorageService.VERSION,
      });
    },
    setAlertListPage: (state, action) => {
      const pageNo = action.payload;
      state.currPage = pageNo < 0 ? 0 : pageNo;
    },
    setAppliedAlertSearch: (state, action) => {
      state.activeSearchParams = {
        id: action.payload.id || state.activeSearchParams.id || null,
        name: action.payload.name || state.activeSearchParams.name || null,
        filters: [...action.payload.filters],
        sort: state.activeSearchParams.sort || null,
        sortType: state.activeSearchParams.sortType || null,
      };
      LocalStorageService.saveStateToLocalStorage(LocalStorageService.ALERT_LIST_KEY, {
        activeSearchParams: state.activeSearchParams,
        rowsPerPage: state.rowsPerPage,
        columnVisibility: state.columnVisibility,
        version: LocalStorageService.VERSION,
      });
    },
    clearAppliedAlertSearch: (state) => {
      state.activeSearchParams = {
        id: null,
        name: null,
        filters: [],
        sort: state.activeSearchParams.sort || null,
        sortType: state.activeSearchParams.sortType || null,
      };
      LocalStorageService.saveStateToLocalStorage(LocalStorageService.ALERT_LIST_KEY, {
        activeSearchParams: state.activeSearchParams,
        rowsPerPage: state.rowsPerPage,
        columnVisibility: state.columnVisibility,
        version: LocalStorageService.VERSION,
      });
    },
    clearAlertListSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase('LOAD_LOCAL_USER_STORAGE', (state) => {
        const localStorageState = LocalStorageService
          .getStateFromLocalStorage(LocalStorageService.ALERT_LIST_KEY);
        if (localStorageState && localStorageState.version === LocalStorageService.VERSION) {
          state.rowsPerPage = localStorageState.rowsPerPage || initialState.rowsPerPage;
          state.columnVisibility = localStorageState.columnVisibility
            || initialState.columnVisibility;
          // eslint-disable-next-line max-len
          state.activeSearchParams = localStorageState.activeSearchParams || initialState.activeSearchParams;
        } else {
          localStorage.removeItem(LocalStorageService.ALERT_LIST_KEY);
        }
        state.isLocalStorageLoaded = true;
      })
      .addCase(fetchFilteredAlertList.pending, (state) => {
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(fetchFilteredAlertList.fulfilled, (state, action) => {
        state.crudStatus = CrudStatus.SUCCESS;
        state.alertList = action.payload.searchResult;

        if (!state.activeSearchParams.sort && !state.activeSearchParams.sortType) {
          state.alertList.sort((a, b) => {
            if (a.isSuspended === true) return 1;
            if (b.isSuspended === true) return -1;
            return 0;
          });
        }

        state.totalNumRecords = action.payload.pageInfo.totalRecords;
        state.totalRecordsIdList = action.payload.totalRecordsIdList;
        state.hasNextPage = action.payload.pageInfo.hasNext;
        state.crudStatus = CrudStatus.IDLE;
        // state.activeSearchParams = action.payload.searchRequest;
      })
      .addCase(fetchFilteredAlertList.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.error = [action.payload];
      })
      .addCase(refreshFilteredAlertList.pending, (state) => {
        state.crudOperation = CrudType.BACKGROUND_READ;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(refreshFilteredAlertList.fulfilled, (state, action) => {
        state.crudStatus = CrudStatus.SUCCESS;
        state.alertList = action.payload.searchResult;
        if (!state.activeSearchParams.sort && !state.activeSearchParams.sortType) {
          state.alertList.sort((a, b) => {
            if (a.isSuspended === true) return 1;
            if (b.isSuspended === true) return -1;
            return 0;
          });
        }
        state.totalNumRecords = action.payload.pageInfo.totalRecords;
        state.hasNextPage = action.payload.pageInfo.hasNext;
        state.crudStatus = CrudStatus.IDLE;
      })
      .addCase(refreshFilteredAlertList.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.error = [action.payload];
      });
  },
});

const { actions, reducer } = alertListSlice;

export const {
  setColumnVisibility,
  setRowsPerPage,
  clearAlertListSlice,
  setAlertListPage,
  setAppliedAlertSearch,
  clearAppliedAlertSearch,
} = actions;
export {
  selectAlertListColumnVisibility,
  selectAlertListRowsPerPage,
  refreshFilteredAlertList,
  selectAlertList,
  selectAlertListPageNo,
  selectAlertListTotalResults,
  fetchFilteredAlertList,
  selectAlertListCrudStatus,
  selectActiveSearchParams,
  selectHasNextPage,
  selectIsAlertListLocalStorageLoaded,
  selectAlertFromListById,
  selectAllIds,
};
export default reducer;
